import {
  type ChangeEvent,
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {styled} from 'styled-components';

import type {FrontendTf1Video} from '@repo/shared/api/tf1';
import {groupTf1Seasons} from '@repo/shared/lib/tf1_catalogue';

import {Button, UnthemedButton} from '@repo/shared-web/components/core/button';
import {LoadingIndicator} from '@repo/shared-web/components/core/loading_indicator';
import {notifyError} from '@repo/shared-web/lib/notification';

import {formatBytes} from '@repo/website-frontend/lib/bytes';
import {
  fetchKohLantaCatalogue,
  useKohLantaCatalogue,
} from '@repo/website-frontend/stores/koh_lanta_store';

interface KohLantaPageProps {}
const STATUS = {
  catalogued: 'Catalogue only',
  queued: 'Queued',
  downloading: 'Downloading',
  complete: 'Downloaded',
  blocked: 'Needs attention',
};
const duration = (seconds: number | undefined): string =>
  seconds ? `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s` : '—';

export const KohLantaPage: FC<KohLantaPageProps> = () => {
  const {data, loading, error, loadedAt} = useKohLantaCatalogue();
  const [selectedId, setSelectedId] = useState<string>();
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    void fetchKohLantaCatalogue().catch(notifyError);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void fetchKohLantaCatalogue().catch(notifyError);
    }, 30_000);
    return () => clearInterval(timer);
  }, []);
  const groups = useMemo(() => groupTf1Seasons(data?.seasons ?? [], data?.videos ?? []), [data]);
  const selected = groups.find(group => group.season.id === selectedId) ?? groups[0];
  const videos = useMemo(
    () => selected?.videos.filter(video => filter === 'all' || video.state === filter) ?? [],
    [selected, filter],
  );
  const totalParts = data?.videos.filter(video => video.kind === 'episode').length ?? 0;
  const downloaded =
    data?.videos.filter(video => video.kind === 'episode' && video.state === 'complete').length ??
    0;
  const handleSeason = useCallback((event: MouseEvent<HTMLElement>) => {
    const id = event.currentTarget.getAttribute('data-season-id');
    if (id) setSelectedId(id);
  }, []);
  const handleFilter = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => setFilter(event.currentTarget.value),
    [],
  );

  return (
    <Wrapper>
      <Header>
        <div>
          <Eyebrow>TF1 REPLAYS</Eyebrow>
          <h1>Koh-Lanta</h1>
          <Subtitle>Seasons, episode parts and your download library.</Subtitle>
        </div>
        <Button onClickAsync={fetchKohLantaCatalogue} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Header>
      {error ? <Notice role="alert">{error}</Notice> : null}
      {!data && loading ? (
        <Loading role="status">
          <LoadingIndicator size={20} /> Loading the catalogue…
        </Loading>
      ) : null}
      {data && !data.enabled ? (
        <Empty>The Koh-Lanta catalogue is not configured in this environment.</Empty>
      ) : null}
      {data?.enabled ? (
        <>
          <Summary aria-label="Catalogue summary">
            <div>
              <strong>
                {downloaded}
                <small> / {totalParts}</small>
              </strong>
              <span>episode parts downloaded</span>
            </div>
            <div>
              <strong>{groups.filter(group => group.season.season !== undefined).length}</strong>
              <span>seasons available</span>
            </div>
            <div>
              <strong>{data.videos.filter(video => video.state === 'queued').length}</strong>
              <span>parts queued</span>
            </div>
          </Summary>
          {data.downloadsPaused ? (
            <Notice>Downloads are paused. The catalogue remains available.</Notice>
          ) : null}
          {!data.downloadsPaused && (data.cooldownUntil ?? 0) > Date.now() ? (
            <Notice>
              Downloads are backing off until {new Date(data.cooldownUntil!).toLocaleString()}.
            </Notice>
          ) : null}
          <Layout>
            <Seasons aria-label="Koh-Lanta seasons">
              {groups.map(group => (
                <SeasonButton
                  key={group.season.id}
                  data-season-id={group.season.id}
                  onClick={handleSeason}
                  $active={selected?.season.id === group.season.id}
                  aria-pressed={selected?.season.id === group.season.id}
                >
                  <strong>
                    {group.season.season === undefined
                      ? group.season.label
                      : `Season ${group.season.season}`}
                  </strong>
                  <span>
                    {group.season.season === undefined
                      ? `${group.videos.length} extras`
                      : `${group.downloaded} / ${group.episodeParts} parts downloaded`}
                  </span>
                </SeasonButton>
              ))}
            </Seasons>
            <Content>
              <ContentHeader>
                <div>
                  <h2>
                    {selected?.season.season === undefined
                      ? (selected?.season.label ?? 'Episodes')
                      : `Season ${selected.season.season}`}
                  </h2>
                  <Metadata>{selected?.season.label}</Metadata>
                </div>
                <Filter aria-label="Download status" value={filter} onChange={handleFilter}>
                  <option value="all">All statuses</option>
                  <option value="complete">Downloaded</option>
                  <option value="queued">Queued</option>
                  <option value="downloading">Downloading</option>
                  <option value="blocked">Needs attention</option>
                  <option value="catalogued">Catalogue only</option>
                </Filter>
              </ContentHeader>
              {videos.length ? (
                <TableScroll>
                  <Table>
                    <caption>Episode parts and download status</caption>
                    <thead>
                      <tr>
                        <th>Episode</th>
                        <th>Duration</th>
                        <th>File size</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {videos.map(video => (
                        <EpisodeRow key={video.videoId} video={video} />
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              ) : (
                <Empty>
                  {groups.length
                    ? 'No videos match this status in this season.'
                    : 'The first catalogue scan has not recorded any videos yet.'}
                </Empty>
              )}
            </Content>
          </Layout>
          <Footer>
            {loadedAt ? `Updated ${new Date(loadedAt).toLocaleTimeString()}. ` : ''}Refreshes every
            30 seconds while this tab is visible.
          </Footer>
        </>
      ) : null}
    </Wrapper>
  );
};
KohLantaPage.displayName = 'KohLantaPage';

interface EpisodeRowProps {
  video: FrontendTf1Video;
}
const EpisodeRow: FC<EpisodeRowProps> = ({video}) => (
  <tr>
    <td>
      <EpisodeTitle>
        {video.kind === 'episode' && video.episode !== undefined
          ? `Episode ${video.episode}${video.part ? ` · Part ${video.part}` : ''}`
          : video.title}
      </EpisodeTitle>
      <Source href={video.url} target="_blank" rel="noreferrer">
        {video.title} ↗
      </Source>
      <MobileDetails>
        {duration(video.durationSeconds)}
        {video.bytes ? ` · ${formatBytes(video.bytes, 1)}` : ''}
      </MobileDetails>
    </td>
    <td>{duration(video.durationSeconds)}</td>
    <td>{video.bytes ? formatBytes(video.bytes, 1) : '—'}</td>
    <td>
      <Status $state={video.state}>{STATUS[video.state]}</Status>
      {video.completedAt ? (
        <Metadata>{new Date(video.completedAt).toLocaleString()}</Metadata>
      ) : null}
      {video.retryAt ? <Metadata>Retry {new Date(video.retryAt).toLocaleString()}</Metadata> : null}
      {video.errorCode ? <Metadata>{video.errorCode.replaceAll('_', ' ')}</Metadata> : null}
    </td>
  </tr>
);
EpisodeRow.displayName = 'EpisodeRow';

const Wrapper = styled.main`
  width: 100%;
  max-width: 1260px;
  box-sizing: border-box;
  padding: 32px 28px;
  margin: 0 auto;
  @media (max-width: 600px) {
    padding: 20px 14px;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  h1 {
    font-size: 32px;
    margin: 6px 0 8px;
  }
`;
const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 2px;
  color: #ffc07b;
  font-weight: 600;
`;
const Subtitle = styled.p`
  color: #a6adbb;
  margin: 0;
  font-size: 14px;
`;
const Summary = styled.div`
  display: flex;
  gap: 40px;
  padding: 22px 24px;
  border: 1px solid #ffffff12;
  border-radius: 10px;
  background: #1b202a;
  margin-bottom: 24px;
  div {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  strong {
    font-size: 26px;
    font-variant-numeric: tabular-nums;
  }
  small {
    font-size: 16px;
    color: #8d97a9;
    font-weight: 400;
  }
  span {
    font-size: 12px;
    color: #a6adbb;
  }
  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1.3fr 1fr 1fr;
    gap: 12px;
    padding: 18px;
    strong {
      font-size: 22px;
    }
    span {
      font-size: 11px;
    }
  }
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Seasons = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 6px;
  @media (max-width: 760px) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 6px;
  }
`;
const SeasonButton = styled(UnthemedButton)<{$active: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 13px 16px;
  border-radius: 8px;
  flex-shrink: 0;
  text-align: left;
  background: ${p => (p.$active ? '#293446' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : '#bac2d0')};
  border: 1px solid ${p => (p.$active ? '#8cbfff55' : 'transparent')};
  strong {
    font-size: 14px;
    font-weight: 500;
  }
  span {
    font-size: 11px;
    color: #a5aebd;
  }
  &:hover {
    background: #293446;
  }
  &:focus-visible {
    outline: 2px solid #8cbfff;
    outline-offset: 2px;
  }
`;
const Content = styled.section`
  min-width: 0;
  border: 1px solid #ffffff12;
  border-radius: 10px;
  background: #1b202a;
  overflow: hidden;
`;
const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 20px;
  h2 {
    font-size: 20px;
    margin: 0 0 4px;
  }
`;
const Metadata = styled.div`
  font-size: 11px;
  color: #939eaf;
  margin-top: 5px;
  line-height: 1.5;
`;
const Filter = styled.select`
  background: #252d3a;
  color: #eee;
  border: 1px solid #ffffff25;
  border-radius: 6px;
  padding: 9px 10px;
  font: inherit;
  font-size: 12px;
`;
const TableScroll = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  th,
  td {
    text-align: left;
    padding: 15px 20px;
    border-top: 1px solid #ffffff10;
    vertical-align: top;
  }
  th {
    font-size: 11px;
    color: #929eaf;
    font-weight: 500;
  }
  td:not(:first-child) {
    white-space: nowrap;
  }
  th:first-child,
  td:first-child {
    min-width: 220px;
  }
  td:first-child {
    width: 100%;
  }
  @media (max-width: 600px) {
    th,
    td {
      padding: 13px 14px;
    }
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3) {
      display: none;
    }
    th:first-child,
    td:first-child {
      min-width: 140px;
    }
    td:last-child {
      white-space: normal;
    }
  }
`;
const EpisodeTitle = styled.strong`
  display: block;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
`;
const Source = styled.a`
  display: block;
  font-size: 11px;
  line-height: 1.5;
  color: #a5b9d3;
  margin-top: 4px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const MobileDetails = styled(Metadata)`
  display: none;
  @media (max-width: 600px) {
    display: block;
  }
`;
const Status = styled.span<{$state: FrontendTf1Video['state']}>`
  white-space: nowrap;
  display: inline-block;
  border-radius: 20px;
  padding: 4px 9px;
  font-size: 11px;
  background: ${p =>
    p.$state === 'complete'
      ? '#7bc99a18'
      : p.$state === 'blocked'
        ? '#ff9c9620'
        : p.$state === 'downloading'
          ? '#77b9ff20'
          : '#ffffff0b'};
  color: ${p =>
    p.$state === 'complete'
      ? '#a3e8ba'
      : p.$state === 'blocked'
        ? '#ffb0a9'
        : p.$state === 'downloading'
          ? '#99caff'
          : '#b4bece'};
`;
const Footer = styled.p`
  font-size: 11px;
  color: #8e99aa;
  margin: 18px 0;
`;
const Notice = styled.div`
  padding: 14px 18px;
  background: #ffcc7b12;
  color: #ffda9f;
  border-radius: 8px;
  font-size: 13px;
  margin: 0 0 18px;
`;
const Empty = styled.p`
  color: #a3adbc;
  padding: 20px;
  margin: 0;
  font-size: 14px;
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 0;
  color: #a3adbc;
  font-size: 14px;
`;
